(() => {
  const SELECTOR = '[data-cs-editorial-product]';

  function init(section) {
    if (section.dataset.csInitialized === 'true') return;
    section.dataset.csInitialized = 'true';

    const dataNode = section.querySelector('[data-cs-product-json]');
    if (!dataNode) return;

    let data;
    try {
      data = JSON.parse(dataNode.textContent);
    } catch (error) {
      return;
    }

    const inputs = Array.from(section.querySelectorAll('[data-cs-option-input]'));
    const optionIndexes = [...new Set(inputs.map((input) => Number(input.dataset.optionIndex)))];
    const variantInput = section.querySelector('[name="id"]');
    const submit = section.querySelector('[data-cs-submit]');
    const submitLabel = section.querySelector('[data-cs-submit-label]');
    const soldOutMessage = submit?.querySelector('.sold-out-message');
    const subtitle = section.querySelector('[data-cs-subtitle]');
    const mtoNotice = section.querySelector('[data-cs-mto-notice]');
    const mtoProperty = section.querySelector('[data-cs-mto-property]');
    const mtoFlag = section.querySelector('[data-cs-mto-flag]');
    const mtoPriority = section.querySelector('[data-cs-mto-priority]');
    const fulfillmentType = section.querySelector('[data-cs-fulfillment-type]');
    const slides = Array.from(section.querySelectorAll('[data-cs-slide]'));
    const dots = Array.from(section.querySelectorAll('[data-cs-dot]'));
    const paymentButton = section.querySelector('.cs-editorial-product__payment-button');
    const morePaymentOptionsSlot = section.querySelector('[data-cs-more-payment-options]');
    let slideIndex = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

    function positionMorePaymentOptions() {
      if (!paymentButton || !morePaymentOptionsSlot) return;
      const moreOptions = paymentButton.querySelector('.shopify-payment-button__more-options');
      if (moreOptions && moreOptions.parentElement !== morePaymentOptionsSlot) {
        morePaymentOptionsSlot.appendChild(moreOptions);
      }
    }

    if (paymentButton && morePaymentOptionsSlot) {
      positionMorePaymentOptions();
      const paymentObserver = new MutationObserver(positionMorePaymentOptions);
      paymentObserver.observe(paymentButton, { childList: true, subtree: true });
    }

    function selectedOptions() {
      const values = [];
      optionIndexes.forEach((index) => {
        const checked = inputs.find((input) => Number(input.dataset.optionIndex) === index && input.checked);
        values[index] = checked ? checked.value : null;
      });
      return values;
    }

    function isMadeToOrder(variant) {
      return Boolean(data.enableMadeToOrder && variant?.madeToOrder);
    }

    function canPurchase(variant) {
      return Boolean(variant && (variant.available || isMadeToOrder(variant)));
    }

    function primeSlideImage(index, priority = 'low') {
      if (!slides.length) return;
      const normalizedIndex = (index + slides.length) % slides.length;
      const image = slides[normalizedIndex]?.querySelector('img');
      if (!image) return;
      image.loading = 'eager';
      image.fetchPriority = priority;
    }

    function warmAdjacentImages() {
      if (slides.length < 2) return;
      primeSlideImage(slideIndex + 1);
      if (slides.length > 2) primeSlideImage(slideIndex - 1);
    }

    function setSlide(index) {
      if (!slides.length) return;
      slideIndex = (index + slides.length) % slides.length;
      primeSlideImage(slideIndex, 'high');
      slides.forEach((slide, i) => slide.classList.toggle('is-active', i === slideIndex));
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === slideIndex);
        dot.setAttribute('aria-current', i === slideIndex ? 'true' : 'false');
      });
      window.setTimeout(warmAdjacentImages, 120);
    }

    function setSlideByMedia(mediaId) {
      if (!mediaId) return;
      const index = slides.findIndex((slide) => String(slide.dataset.mediaId) === String(mediaId));
      if (index >= 0) setSlide(index);
    }

    function refreshOptionAvailability(values) {
      inputs.forEach((input) => {
        const optionIndex = Number(input.dataset.optionIndex);
        const matchingVariants = data.variants.filter((variant) => {
          if (variant.options[optionIndex] !== input.value) return false;
          return values.every((value, index) => index === optionIndex || value == null || variant.options[index] === value);
        });

        const standardPossible = matchingVariants.some((variant) => variant.available && !isMadeToOrder(variant));
        const mtoPossible = matchingVariants.some((variant) => isMadeToOrder(variant));
        const selectable = matchingVariants.some(canPurchase);
        const mtoOnly = mtoPossible && !standardPossible;
        const label = input.nextElementSibling;

        input.disabled = !selectable;
        label?.classList.toggle('is-made-to-order-option', mtoOnly);
        if (label) {
          label.title = mtoOnly ? `${input.value} — Made to order` : '';
        }
      });
    }

    function setFulfillmentProperties(madeToOrder, hasVariant = true) {
      const mtoFields = [mtoProperty, mtoFlag, mtoPriority].filter(Boolean);
      mtoFields.forEach((field) => { field.disabled = !madeToOrder || !hasVariant; });

      if (mtoProperty) mtoProperty.value = madeToOrder && hasVariant ? data.madeToOrderProperty : '';
      if (mtoFlag) mtoFlag.value = madeToOrder && hasVariant ? 'YES' : '';
      if (mtoPriority) mtoPriority.value = madeToOrder && hasVariant ? data.madeToOrderPriority : '';

      if (fulfillmentType) {
        fulfillmentType.disabled = !hasVariant;
        fulfillmentType.value = hasVariant ? (madeToOrder ? 'MADE TO ORDER' : 'PREMADE') : '';
      }
    }

    function resetSubmitMessage() {
      submitLabel?.classList.remove('hidden');
      soldOutMessage?.classList.add('hidden');
    }

    function update() {
      const values = selectedOptions();
      const selectionComplete = !inputs.length || optionIndexes.every((index) => values[index] != null);
      const variant = inputs.length
        ? (selectionComplete
          ? data.variants.find((item) => item.options.every((value, index) => value === values[index]))
          : null)
        : data.variants.find((item) => String(item.id) === String(data.initialVariantId)) || data.variants[0];

      if (inputs.length) refreshOptionAvailability(values);
      if (subtitle && data.colorOptionIndex >= 0) subtitle.textContent = values[data.colorOptionIndex] || '';

      if (variantInput) {
        const nextVariantId = variant ? String(variant.id) : '';
        const variantChanged = variantInput.value !== nextVariantId;
        variantInput.value = nextVariantId;
        if (variantChanged) variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      resetSubmitMessage();

      if (!variant) {
        section.classList.remove('is-made-to-order');
        section.classList.add('has-no-selection');
        if (mtoNotice) mtoNotice.hidden = true;
        setFulfillmentProperties(false, false);
        submit?.classList.remove('is-made-to-order');
        submit?.setAttribute('aria-disabled', 'true');
        if (submit) submit.disabled = true;
        if (submitLabel) submitLabel.textContent = selectionComplete ? data.unavailableLabel : data.selectLabel;

        if (window.history && window.history.replaceState) {
          const url = new URL(window.location.href);
          url.searchParams.delete('variant');
          window.history.replaceState({}, '', url.toString());
        }
        return;
      }

      section.classList.remove('has-no-selection');
      const madeToOrder = isMadeToOrder(variant);
      section.classList.toggle('is-made-to-order', madeToOrder);
      submit?.classList.toggle('is-made-to-order', madeToOrder);
      if (mtoNotice) mtoNotice.hidden = !madeToOrder;
      setFulfillmentProperties(madeToOrder, true);

      if (canPurchase(variant)) {
        submit?.removeAttribute('aria-disabled');
        if (submit) submit.disabled = false;
        if (submitLabel) {
          const label = madeToOrder ? data.madeToOrderLabel : data.addLabel;
          submitLabel.textContent = `${label} — ${variant.price}`;
        }
      } else {
        submit?.setAttribute('aria-disabled', 'true');
        if (submit) submit.disabled = true;
        if (submitLabel) submitLabel.textContent = data.soldOutLabel;
      }

      setSlideByMedia(variant.featuredMediaId);
      if (window.history && window.history.replaceState) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', variant.id);
        window.history.replaceState({}, '', url.toString());
      }
    }

    inputs.forEach((input) => input.addEventListener('change', update));
    dots.forEach((dot, index) => dot.addEventListener('click', () => setSlide(index)));

    const slidesWrap = section.querySelector('[data-cs-slides]');
    let pointerStartX = null;
    let activePointerId = null;

    function finishPointerGesture(event) {
      if (pointerStartX == null || (activePointerId != null && event.pointerId !== activePointerId)) return;
      const delta = event.clientX - pointerStartX;
      if (Math.abs(delta) > 45) setSlide(slideIndex + (delta < 0 ? 1 : -1));
      slidesWrap?.classList.remove('is-dragging');
      pointerStartX = null;
      activePointerId = null;
    }

    slidesWrap?.addEventListener('pointerdown', (event) => {
      if (slides.length < 2 || (event.pointerType === 'mouse' && event.button !== 0)) return;
      pointerStartX = event.clientX;
      activePointerId = event.pointerId;
      slidesWrap.classList.add('is-dragging');
      slidesWrap.setPointerCapture?.(event.pointerId);
    });
    slidesWrap?.addEventListener('pointerup', finishPointerGesture);
    slidesWrap?.addEventListener('pointercancel', (event) => {
      if (activePointerId != null && event.pointerId !== activePointerId) return;
      slidesWrap.classList.remove('is-dragging');
      pointerStartX = null;
      activePointerId = null;
    });
    slidesWrap?.addEventListener('dragstart', (event) => event.preventDefault());
    slidesWrap?.addEventListener('keydown', (event) => {
      if (slides.length < 2) return;
      if (event.key === 'ArrowRight') {
        event.preventDefault();
        setSlide(slideIndex + 1);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        setSlide(slideIndex - 1);
      }
    });

    update();

    const warmInitialGallery = () => warmAdjacentImages();
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(warmInitialGallery, { timeout: 1400 });
    } else {
      window.setTimeout(warmInitialGallery, 650);
    }
  }

  function initAll(root = document) {
    root.querySelectorAll(SELECTOR).forEach(init);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
